import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  clearStoredAuthData,
  getStoredAuthData,
  saveAuthData,
  clearAuthFlowLocalContext,
} from 'js/library/services/authSession';
import { getCurrentUserInfo } from 'js/library/services/currentUserInfo';
import ErrorMessage from 'js/components/DataConfirmation/ErrorMessage';
import { logout as logoutAPI } from 'js/library/utils/API/auth/logout';
import { linkGuest } from 'js/library/utils/API/auth/linkGuest';
import {
  clearGuestData,
  getGuestData,
} from 'js/library/utils/API/auth/generateGuest';
import {
  ACCESS_TOKEN_REFRESH_LEAD_TIME_MS,
  getAccessTokenExpiration,
  isAccessTokenDueForRefresh,
  refreshAccessToken,
  subscribeToAccessTokenUpdates,
} from 'js/library/utils/API/auth/authSessionManager';
import createGuest from 'js/library/utils/API/createGuest';
import { validateSessions } from 'js/library/utils/API/auth/validateSessions';
import { getPreData } from 'js/library/utils/API/auth/preData';
import { editUserPartner } from 'js/library/utils/API/apiEditUserPartner';
import cfac22 from 'js/library/utils/cfac22';

const isEmptyValue = (value) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

const mergeEmptyValues = (current, incoming) => {
  if (Array.isArray(current) || Array.isArray(incoming)) {
    const currentList = Array.isArray(current) ? current : [];
    const incomingList = Array.isArray(incoming) ? incoming : [];
    const entries = new Map(currentList.map((item) => [item?.type || item?.optInId, item]));

    incomingList.forEach((item) => {
      const key = item?.type || item?.optInId;
      if (!key) return;
      entries.set(key, entries.has(key) ? mergeEmptyValues(entries.get(key), item) : item);
    });

    return Array.from(entries.values());
  }

  if (current && typeof current === 'object' && incoming && typeof incoming === 'object') {
    const merged = { ...current };
    Object.entries(incoming).forEach(([key, value]) => {
      merged[key] = key in merged ? mergeEmptyValues(merged[key], value) : value;
    });
    return merged;
  }

  return isEmptyValue(current) ? incoming : current;
};

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const initialAuthState = {
  authData: null,
  guestData: null,
  identify: {
    identifier: null,
    data: null,
  },
  requestCode: {
    cellPhone: null,
    id: null,
    newUser: false,
  },
  preData: {},
  registrationDraft: null,
  currentUserInfo: null,
};

const AuthContext = createContext(null);

const getCompleteStoredAuthData = () => {
  const authData = getStoredAuthData();

  return authData?.accessToken && authData?.sessionId && authData?.userUId ? authData : null;
};

const getCompleteStoredGuestData = () => {
  const guestData = getGuestData();

  return guestData?.guestId && guestData?.guestToken ? guestData : null;
};

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(() => ({
    ...initialAuthState,
    authData: getCompleteStoredAuthData(),
    guestData: getCompleteStoredGuestData(),
  }));
  const [authInitialized, setAuthInitialized] = useState(() => {
    return Boolean(getCompleteStoredAuthData() || getCompleteStoredGuestData());
  });
  const [authInitError, setAuthInitError] = useState(null);
  const guestInitInFlight = useRef(null);
  const refreshingTokenRef = useRef(false);

  const initializeGuestSession = useCallback(async () => {
    if (guestInitInFlight.current) return guestInitInFlight.current;

    const initializationPromise = (async () => {
      setAuthInitError(null);
      setAuthInitialized(false);

      try {
        const guestData = await createGuest();

        if (!guestData) {
          throw new Error('Não foi possível criar a sessão de visitante.');
        }

        setAuthState((currentState) => ({
          ...currentState,
          guestData,
        }));
      } catch (error) {
        setAuthInitError(error);
        setAuthState((currentState) => ({
          ...currentState,
          guestData: null,
        }));
      } finally {
        setAuthInitialized(true);
      }
    })();

    guestInitInFlight.current = initializationPromise;
    initializationPromise.then(
      () => {
        if (guestInitInFlight.current === initializationPromise) {
          guestInitInFlight.current = null;
        }
      },
      () => {
        if (guestInitInFlight.current === initializationPromise) {
          guestInitInFlight.current = null;
        }
      }
    );

    return initializationPromise;
  }, []);

  useEffect(() => {
    if (authState.authData) {
      setAuthInitialized(true);
      return;
    }

    if (!authInitialized) {
      initializeGuestSession();
    }
  }, [authInitialized, authState.authData, initializeGuestSession]);

  // Carregar informações do usuário quando authData existir mas currentUserInfo não
  useEffect(() => {
    if (!authState.authData || authState.currentUserInfo) {
      return;
    }

    (async () => {
      try {
        const userInfo = await getCurrentUserInfo({ authData: authState.authData });
        setAuthState((currentState) => ({
          ...currentState,
          currentUserInfo: userInfo,
        }));
      } catch (error) {
        console.error('Erro ao carregar informações do usuário:', error);
      }
    })();
  }, [authState.authData, authState.currentUserInfo]);

  const updateAccessToken = useCallback((accessToken) => {
    if (!accessToken) return;

    setAuthState((currentState) => {
      if (!currentState.authData) return currentState;

      const updatedAuthData = {
        ...currentState.authData,
        accessToken,
      };

      saveAuthData(updatedAuthData);

      return {
        ...currentState,
        authData: updatedAuthData,
      };
    });
  }, []);

  // Função para fazer refresh do token se necessário
  const refreshAccessTokenIfNeeded = useCallback(async () => {
    const accessToken = authState.authData?.accessToken;

    if (!accessToken) return;
    if (!isAccessTokenDueForRefresh(accessToken)) return;
    if (refreshingTokenRef.current) return;

    refreshingTokenRef.current = true;

    try {
      const newAccessToken = await refreshAccessToken();
      updateAccessToken(newAccessToken);
    } catch {
      // O gerenciador limpa a sessão e redireciona em caso de falha.
    } finally {
      refreshingTokenRef.current = false;
    }
  }, [authState.authData?.accessToken, updateAccessToken]);

  // Agenda o refresh preventivo pela expiração do token.
  useEffect(() => {
    const accessToken = authState.authData?.accessToken;

    if (!accessToken) return;

    refreshAccessTokenIfNeeded();

    const expiresAt = getAccessTokenExpiration(accessToken);
    if (expiresAt === null) return undefined;

    const refreshDelay = Math.max(
      0,
      expiresAt - Date.now() - ACCESS_TOKEN_REFRESH_LEAD_TIME_MS
    );
    const refreshTimeoutId = setTimeout(() => {
      refreshAccessTokenIfNeeded();
    }, refreshDelay);

    return () => {
      clearTimeout(refreshTimeoutId);
    };
  }, [authState.authData?.accessToken, refreshAccessTokenIfNeeded]);

  useEffect(
    () =>
      subscribeToAccessTokenUpdates((accessToken) => {
        if (!accessToken) return;

        setAuthState((currentState) => {
          if (!currentState.authData) return currentState;

          const updatedAuthData = {
            ...currentState.authData,
            accessToken,
          };

          return {
            ...currentState,
            authData: updatedAuthData,
          };
        });
      }),
    []
  );

  // Mantém a sessão validada enquanto a aplicação estiver aberta e ao voltar
  // para a aba, janela, cache do navegador ou conexão.
  useEffect(() => {
    if (!authState.authData?.accessToken) return undefined;

    const validateWhileVisible = () => {
      if (document.visibilityState === 'hidden') return;

      refreshAccessTokenIfNeeded();
      validateSessions()
        .then((validation) => {
          const refreshedAccessToken = validation?.data?.accessToken;

          if (validation?.requiresRefresh && refreshedAccessToken) {
            updateAccessToken(refreshedAccessToken);
          }
        })
        .catch(() => null);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') validateWhileVisible();
    };

    const handleFocus = () => validateWhileVisible();
    const handlePageShow = () => validateWhileVisible();
    const handleOnline = () => validateWhileVisible();
    const intervalId = setInterval(validateWhileVisible, 30 * 1000);

    validateWhileVisible();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('online', handleOnline);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('online', handleOnline);
    };
  }, [authState.authData?.accessToken, refreshAccessTokenIfNeeded, updateAccessToken]);

  const setIdentifyAuth = useCallback(({ identifier, data }) => {
    setAuthState((currentState) => ({
      ...currentState,
      identify: {
        identifier,
        data,
      },
    }));
  }, []);

  const enrichAfterAuthentication = useCallback(async ({ authData, partnerId, identifier }) => {
    if (!partnerId || !identifier || !authData?.userUId) return;

    try {
      const preData = await getPreData({ partnerId, identifier });
      setAuthState((currentState) => {
        const enrichedUserInfo = mergeEmptyValues(currentState.currentUserInfo, preData);

        if (!isEqual(currentState.currentUserInfo, enrichedUserInfo)) {
          editUserPartner(authData.userUId, cfac22('clubId'), enrichedUserInfo).catch((error) =>
            console.error('Erro ao atualizar dados enriquecidos do usuário:', error)
          );
        }

        return { ...currentState, preData, currentUserInfo: enrichedUserInfo };
      });
    } catch (error) {
      console.error('Erro ao enriquecer dados do usuário:', error);
    }
  }, []);

  const setAuthData = useCallback(async (authData) => {
    const hadGuestData = Boolean(authState.guestData);

    // Salvar no localStorage
    saveAuthData(authData);

    // Buscar informações do usuário
    let userInfo = null;
    try {
      userInfo = await getCurrentUserInfo({ authData });
    } catch (error) {
      console.error('Erro ao carregar informações do usuário:', error);
    }

    // Atualizar estado com authData, limpar guestData e setar userInfo
    setAuthState({
      ...initialAuthState,
      authData,
      guestData: null,
      currentUserInfo: userInfo,
    });
    setAuthInitialized(true);

    // Se tinha guest, vincular ao usuário logado
    if (hadGuestData) {
      try {
        await linkGuest();

        // Limpar os dados persistidos da guest após o vínculo.
        clearGuestData();
      } catch (error) {
        console.error('Erro ao vincular guest:', error);
      }
    }

    return userInfo;
  }, [authState.guestData]);

  const setRequestCodeAuth = useCallback(({ cellPhone, id, newUser }) => {
    setAuthState((currentState) => ({
      ...currentState,
      requestCode: {
        cellPhone,
        id,
        newUser,
      },
    }));
  }, []);

  const setRegistrationDraft = useCallback((registrationDraft) => {
    setAuthState((currentState) => ({
      ...currentState,
      registrationDraft,
    }));
  }, []);

  const setCurrentUserInfo = useCallback((currentUserInfo) => {
    setAuthState((currentState) => ({
      ...currentState,
      currentUserInfo,
    }));
  }, []);

  const logout = useCallback(async () => {
    try {
      const response = await logoutAPI();
      if (!response?.success) {
        const error = response?.error || 'Erro ao realizar logout';
        console.error('Falha ao realizar logout:', error);
        return { success: false, error };
      }

    } catch (error) {
      console.error('Erro ao chamar API de logout:', error);
      return { success: false, error };
    }

    // Limpar localStorage
    clearStoredAuthData();
    clearAuthFlowLocalContext();
    clearGuestData();

    // Forcar reload completo para evitar reidratacao de dados na SPA apos logout
    if (window.location.pathname === '/' && !window.location.search && !window.location.hash) {
      window.location.reload();
    } else {
      window.location.replace('/');
    }

    return { success: true };
  }, []);

  const value = useMemo(
    () => ({
      authState,
      authInitialized,
      authInitError,
      setIdentifyAuth,
      setRequestCodeAuth,
      setAuthData,
      enrichAfterAuthentication,
      setRegistrationDraft,
      setCurrentUserInfo,
      updateAccessToken,
      logout,
    }),
    [
      authState,
      authInitialized,
      authInitError,
      enrichAfterAuthentication,
      logout,
      setAuthData,
      setCurrentUserInfo,
      setIdentifyAuth,
      setRegistrationDraft,
      setRequestCodeAuth,
      updateAccessToken,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {authInitialized && !authInitError && (authState.authData || authState.guestData)
        ? children
        : null}
      {authInitialized && authInitError ? (
        <ErrorMessage
          message="Não foi possível iniciar a sessão. Tente novamente em alguns instantes."
          onRetry={initializeGuestSession}
        />
      ) : null}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }

  return context;
}
